import { z } from 'zod'

/**
 * "Fill from document" on the New Contract dialog: Benny reads one attached
 * file and proposes the contract essentials. The proposal is held client-side
 * until the user applies it — nothing is written by the extraction itself.
 */

/** AI-credits feature key. Priced in the API's TWO_CREDIT_FEATURES set. */
export const CONTRACT_EXTRACTION_FEATURE = 'extract-contract'

/** Content types the extraction reads. Images are deliberately excluded: only
 * one Bedrock model reads them and it refuses most scans. */
export const CONTRACT_EXTRACTION_CONTENT_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const

/** Bedrock Converse caps a document block at 4.5 MB. */
export const CONTRACT_EXTRACTION_MAX_BYTES = 4.5 * 1024 * 1024

export interface ContractExtractionRequest {
  fileName: string
  contentType: string
  /** Raw base64 (no data-URL prefix). */
  base64: string
  /** The org's contract type names; the model picks one or returns null. */
  contractTypes?: string[]
}

const optionalText = z.string().trim().min(1).nullable()
/** Date-only strings; the dialog parses them at local noon. */
const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()

export const ContractExtractionResultSchema = z.object({
  title: optionalText,
  contractNumber: optionalText,
  description: optionalText,
  startDate: optionalDate,
  endDate: optionalDate,
  totalValue: z.number().nonnegative().nullable(),
  /** ISO 4217 code. */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  /** One of the request's contractTypes, or null. */
  contractType: optionalText,
  paymentTerms: optionalText,
  renewalTerms: optionalText,
  isRenewable: z.boolean().nullable(),
  renewalDueDate: optionalDate,
  signedByEntityName: optionalText,
  signedByOrgName: optionalText,
  signedByClient: optionalDate,
  signedByOrg: optionalDate,
  /** One sentence on what the document is. */
  summary: optionalText,
})

export type ContractExtractionResult = z.infer<
  typeof ContractExtractionResultSchema
>

export type ContractExtractionField = Exclude<
  keyof ContractExtractionResult,
  'summary'
>

export const CONTRACT_EXTRACTION_FIELD_LABELS: Record<
  ContractExtractionField,
  string
> = {
  title: 'Title',
  contractNumber: 'Contract number',
  description: 'Description',
  startDate: 'Start date',
  endDate: 'End date',
  totalValue: 'Total value',
  currency: 'Currency',
  contractType: 'Contract type',
  paymentTerms: 'Payment terms',
  renewalTerms: 'Renewal terms',
  isRenewable: 'Renewable',
  renewalDueDate: 'Renewal due',
  signedByEntityName: 'Signed by client',
  signedByOrgName: 'Signed by organization',
  signedByClient: 'Client signed on',
  signedByOrg: 'Organization signed on',
}
